/**
 * Clinic finder — powers both the emergency-booking lightbox (#emergency-lightbox)
 * and the `clinic-finder` page block. Any element with [data-clinic-finder] is a
 * mount; [data-cf-mode] picks the behaviour when a clinic is chosen:
 *
 *   "emergency" → load that clinic's Dentli emergency widget + phone sidetext
 *   "finder"    → show clinic actions (clinic page, "Book time", phone/mail/map)
 *
 * The sheet open/close/drag (emergency only) is handled by the shared lightbox.js.
 * Markup: partials/clinic-finder-body.php · styles: components/emergency-lightbox.css
 *
 * NOTE: strings here are not run through WP i18n — wire wp_localize_script if
 * they need translating.
 */

import { mountDentliWidget } from '@/js/dentli.js';
import { showClinicMap } from '@/js/clinic-map.js';

const TEXT = {
	locating: 'Henter posisjonen din…',
	denied: 'Posisjon utilgjengelig — velg et sted nedenfor i stedet.',
	unsupported: 'Nettleseren din støtter ikke deling av posisjon — velg et sted nedenfor.',
	none: 'Ingen klinikker med koordinater i nærheten.',
	empty: 'Ingen klinikker funnet.',
	back: 'Vis alle klinikker',
	resultsLabel: 'Dine resultater',
	clinicWord: ( n ) => (n === 1 ? 'klinikk' : 'klinikker'),
	clinicPage: 'Gå til klinikkside',
	bookTime: 'Bestill time',
	callAria: 'Ring klinikken',
	emailAria: 'Send e-post til klinikken',
	mapAria: 'Vis klinikken på kart',
};

const svgIcon = ( paths ) =>
	'<svg class="block w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor"' +
	' stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
	paths +
	'</svg>';

const CHEVRON =
	'<svg class="h-3 w-3 shrink-0" viewBox="0 0 280 512" fill="currentColor" aria-hidden="true">' +
	'<path d="M40.24 505.14c-9.18 8.82-24.12 8.82-33.35-.41-9.19-9.23-9.19-24.17 0-33.35L222.55 255.72 6.89 40.24C-2.3 31.06-2.3 16.12 6.89 6.89 16.12-2.3 31.06-2.3 40.24 6.89L272.7 239.35c4.37 4.37 6.9 10.37 6.9 16.37s-2.53 12-6.9 16.37L40.24 505.14Z"/>' +
	'</svg>';
const ICON_PHONE = svgIcon( '<path d="M6.5 3h3l1.5 5-2 1.5a12 12 0 0 0 5 5l1.5-2 5 1.5v3a2 2 0 0 1-2 2A16 16 0 0 1 4.5 5a2 2 0 0 1 2-2Z"/>' );
const ICON_MAIL = svgIcon( '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="m4 7 8 6 8-6"/>' );
const ICON_MAP = svgIcon( '<path d="M12 21s7-6.5 7-12a7 7 0 1 0-14 0c0 5.5 7 12 7 12Z"/><circle cx="12" cy="9" r="2.5"/>' );

function escapeHtml( value ) {
	const el = document.createElement( 'div' );
	el.textContent = value == null ? '' : String( value );
	return el.innerHTML;
}

function escapeAttr( value ) {
	return String( value == null ? '' : value ).replace(
		/[&<>"]/g,
		( c ) => ({
			'&': '&amp;',
			'<': '&lt;',
			'>': '&gt;',
			'"': '&quot;',
		}[ c ]),
	);
}

function telHref( phone ) {
	const digits = String( phone ).replace( /[^\d+]/g, '' );
	if ( digits.startsWith( '+' ) || digits.startsWith( '00' ) ) {
		return 'tel:' + digits;
	}
	return 'tel:0047' + digits.replace( /^0/, '' );
}

function distanceKm( a, b ) {
	const R = 6371;
	const toRad = ( d ) => (d * Math.PI) / 180;
	const dLat = toRad( b.lat - a.lat );
	const dLng = toRad( b.lng - a.lng );
	const h =
		Math.sin( dLat / 2 ) ** 2 +
		Math.cos( toRad( a.lat ) ) * Math.cos( toRad( b.lat ) ) * Math.sin( dLng / 2 ) ** 2;
	return 2 * R * Math.asin( Math.sqrt( h ) );
}

const DISC_CLASS =
	'shrink-0 flex! justify-center items-center size-11 rounded-full transition-colors ' +
	'bg-white text-brand-odontia-bla hover:bg-brand-odontia-bla hover:text-white';

function actionDisc( href, label, icon, external = false ) {
	const rel = external ? ' target="_blank" rel="noopener"' : '';
	return `<a href="${escapeAttr( href )}"${rel} class="${DISC_CLASS}" aria-label="${escapeAttr( label )}">${icon}</a>`;
}

function initClinicFinder( root ) {
	const mode = root.dataset.cfMode === 'finder' ? 'finder' : 'emergency';

	const dataEl = root.querySelector( '[data-emergency-data]' );
	const results = root.querySelector( '[data-emergency-results]' );
	const geoBtn = root.querySelector( '[data-emergency-geo]' );
	const geoStatus = root.querySelector( '[data-emergency-geo-status]' );
	const locationBtns = [...root.querySelectorAll( '[data-emergency-location]' )];
	// Desktop split layout only (partials/clinic-finder-body.php, 'split').
	const searchInput = root.querySelector( '[data-cf-search]' );
	const viewBtns = [...root.querySelectorAll( '[data-cf-view]' )];
	const mapEl = root.querySelector( '[data-cf-map]' );
	const desktop = window.matchMedia( '(min-width: 64rem)' );
	let view = 'list';

	if ( !dataEl || !results ) {
		return;
	}

	let clinics = [];
	try {
		clinics = JSON.parse( dataEl.textContent.trim() );
	} catch ( err ) {
		return;
	}

	const resultsPlaceholder = results.innerHTML;
	let lastList = null;

	const setStatus = ( message ) => {
		if ( !geoStatus ) {
			return;
		}
		geoStatus.textContent = message || '';
		geoStatus.classList.toggle( 'hidden', !message );
	};

	const clearLocationButtons = () => {
		locationBtns.forEach( ( btn ) => btn.setAttribute( 'aria-pressed', 'false' ) );
	};

	const closingInfo = ( clinic ) => {
		const tpl = root.querySelector( `template[data-emergency-closing="${clinic.id}"]` );
		return tpl ? tpl.content.cloneNode( true ) : null;
	};

	const emergencyRow = ( clinic ) => {
		const li = document.createElement( 'li' );
		li.innerHTML =
			'<div class="flex items-end gap-2 p-xxs bg-brand-fersken rounded-[16px]">' +
			`<button type="button" class="flex-1 flex flex-col gap-0.5 link-reset text-left" data-emergency-pick="${clinic.id}">` +
			`<span class="text-body-xl font-medium leading-heading">${escapeHtml( clinic.title )}</span>` +
			'</button>' +
			`<span class="shrink-0 grid place-items-center size-6 rounded-full text-brand-odontia-bla/60" aria-hidden="true">${CHEVRON}</span>` +
			'</div>';

		const info = closingInfo( clinic );
		if ( info ) {
			li.querySelector( '[data-emergency-pick]' ).appendChild( info );
		}
		return li;
	};

	const finderRow = ( clinic ) => {
		const li = document.createElement( 'li' );

		const pageLink = clinic.permalink
			? `<a href="${escapeAttr( clinic.permalink )}" class="flex! shrink-0 items-center gap-3 text-body-sm whitespace-nowrap">${escapeHtml( TEXT.clinicPage )}${CHEVRON}</a>`
			: '';

		const discs =
			(clinic.phone ? actionDisc( telHref( clinic.phone ), TEXT.callAria, ICON_PHONE ) : '') +
			(clinic.email ? actionDisc( 'mailto:' + clinic.email, TEXT.emailAria, ICON_MAIL ) : '') +
			(clinic.mapsUrl ? actionDisc( clinic.mapsUrl, TEXT.mapAria, ICON_MAP, true ) : '');

		li.innerHTML =
			'<div class="p-xxs bg-brand-dus-gronn rounded-[16px]">' +
			'<div class="flex flex-col gap-3">' +
			`<div class="text-body-xl font-medium leading-heading">${escapeHtml( clinic.title )}</div>` +
			'<div class="flex gap-2 justify-between">' +
			'<div data-cf-clinic-head></div>' +
			pageLink +
			'</div>' +
			'</div>' +
			'<div class="flex items-center justify-between gap-2 mt-3">' +
			(clinic.bookUrl
				? `<a href="${escapeAttr( clinic.bookUrl )}" target="_blank" rel="noopener" class="btn btn-primary px-xxs!">${escapeHtml( TEXT.bookTime )}</a>`
				: '<span></span>') +
			`<div class="flex gap-2.5">${discs}</div>` +
			'</div>' +
			'</div>';

		const info = closingInfo( clinic );
		if ( info ) {
			li.querySelector( '[data-cf-clinic-head]' ).appendChild( info );
		}
		return li;
	};

	const clinicRow = mode === 'finder' ? finderRow : emergencyRow;

	// Map view: the results' count row stays, the card list is swapped for the map.
	const syncMap = () => {
		if ( !mapEl ) {
			return;
		}
		const onMap = view === 'map' && desktop.matches;
		mapEl.classList.toggle( 'hidden', !onMap );
		results.querySelector( 'ul' )?.classList.toggle( 'hidden', onMap );
		// Nothing chosen yet: the list shows the "choose a location" note, the
		// map simply shows every clinic (the note would only sit above it).
		results.classList.toggle( 'hidden', onMap && !lastList );
		// …and the map then takes over the results' gap below the List / Map tabs
		// (lg:mt-md is the results block's own top margin in clinic-finder-body.php).
		mapEl.classList.toggle( 'lg:mt-md', onMap && !lastList );
		if ( onMap ) {
			showClinicMap( mapEl, lastList || clinics, { clinicPageLabel: TEXT.clinicPage } );
		}
	};

	const renderClinics = ( list ) => {
		lastList = list;
		root.classList.remove( 'is-panel' );
		root.scrollTop = 0;

		if ( !list.length ) {
			results.innerHTML = `<p class="text-body-sm py-2 text-brand-odontia-bla/60">${escapeHtml( TEXT.empty )}</p>`;
			syncMap();
			return;
		}

		const count = document.createElement( 'p' );
		count.className = 'flex justify-between gap-2 py-2 border-b border-b-brand-odontia-bla/60';
		count.innerHTML =
			`<span>${escapeHtml( TEXT.resultsLabel )}</span>` +
			`<span><span class="font-medium">${list.length}</span> ${escapeHtml( TEXT.clinicWord( list.length ) )}</span>`;

		const ul = document.createElement( 'ul' );
		ul.className = 'w-full flex flex-col gap-4 mt-2';
		list.forEach( ( clinic ) => ul.appendChild( clinicRow( clinic ) ) );

		results.replaceChildren( count, ul );
		syncMap();
	};

	const showClinic = ( id ) => {
		const clinic = clinics.find( ( c ) => String( c.id ) === String( id ) );
		if ( !clinic ) {
			return;
		}

		const wrap = document.createElement( 'div' );
		wrap.className = 'py-xs';
		wrap.innerHTML =
			`<button type="button" class="text-body-sm underline mb-4" data-emergency-back>${escapeHtml( TEXT.back )}</button>` +
			'<div data-emergency-widget-mount></div>';
		results.replaceChildren( wrap );
		root.classList.add( 'is-panel' );
		root.scrollTop = 0;

		if ( clinic.widgetId ) {
			mountDentliWidget(
				wrap.querySelector( '[data-emergency-widget-mount]' ),
				clinic.widgetId,
				clinic.treatmentId,
			);
		}

		const subtextTpl = root.querySelector( `template[data-emergency-subtext="${clinic.id}"]` );
		if ( subtextTpl ) {
			wrap.appendChild( subtextTpl.content.cloneNode( true ) );
		}
	};

	// --- events -----------------------------------------------------------

	if ( mode === 'emergency' ) {
		results.addEventListener( 'click', ( event ) => {
			const pick = event.target.closest( '[data-emergency-pick]' );
			if ( pick ) {
				showClinic( pick.getAttribute( 'data-emergency-pick' ) );
				return;
			}
			if ( event.target.closest( '[data-emergency-back]' ) && lastList ) {
				renderClinics( lastList );
			}
		} );

		// Reset to the initial state whenever the lightbox is closed.
		new MutationObserver( () => {
			if ( !root.classList.contains( 'is-open' ) ) {
				clearLocationButtons();
				setStatus( '' );
				results.innerHTML = resultsPlaceholder;
				lastList = null;
				if ( root.classList.contains( 'is-panel' ) ) {
					root.classList.remove( 'is-panel' );
				}
			}
		} ).observe( root, {
			attributes: true,
			attributeFilter: ['class'],
		} );
	}

	const showPlaceholder = () => {
		results.innerHTML = resultsPlaceholder;
		lastList = null;
		root.classList.remove( 'is-panel' );
		syncMap();
	};

	locationBtns.forEach( ( btn ) => {
		btn.addEventListener( 'click', () => {
			const wasOn = btn.getAttribute( 'aria-pressed' ) === 'true';
			clearLocationButtons();
			setStatus( '' );
			if ( searchInput ) {
				searchInput.value = '';
			}

			if ( wasOn ) {
				showPlaceholder();
				return;
			}

			btn.setAttribute( 'aria-pressed', 'true' );
			const id = btn.getAttribute( 'data-emergency-location' );
			renderClinics( clinics.filter( ( c ) => String( c.locationId ) === String( id ) ) );
		} );
	} );

	if ( geoBtn ) {
		geoBtn.addEventListener( 'click', () => {
			if ( !navigator.geolocation ) {
				setStatus( TEXT.unsupported );
				return;
			}

			setStatus( TEXT.locating );
			clearLocationButtons();

			navigator.geolocation.getCurrentPosition(
				( position ) => {
					const here = {
						lat: position.coords.latitude,
						lng: position.coords.longitude,
					};
					const nearest = clinics
						.filter( ( c ) => c.lat != null && c.lng != null )
						.map( ( c ) => ({
							clinic: c,
							d: distanceKm( here, c ),
						}) )
						.sort( ( a, b ) => a.d - b.d )
						.slice( 0, 5 )
						.map( ( entry ) => entry.clinic );

					setStatus( nearest.length ? '' : TEXT.none );
					renderClinics( nearest );
				},
				() => setStatus( TEXT.denied ),
				{
					enableHighAccuracy: true,
					timeout: 10000,
				},
			);
		} );
	}

	if ( searchInput ) {
		searchInput.addEventListener( 'input', () => {
			const q = searchInput.value.trim().toLocaleLowerCase( 'nb' );
			clearLocationButtons();
			setStatus( '' );
			if ( !q ) {
				showPlaceholder();
				return;
			}
			renderClinics( clinics.filter( ( c ) => `${c.title} ${c.location || ''}`.toLocaleLowerCase( 'nb' ).includes( q ) ) );
		} );
	}

	viewBtns.forEach( ( btn ) => {
		btn.addEventListener( 'click', () => {
			view = btn.dataset.cfView === 'map' ? 'map' : 'list';
			viewBtns.forEach( ( b ) => b.setAttribute( 'aria-pressed', String( b === btn ) ) );
			syncMap();
		} );
	} );

	if ( mapEl ) {
		desktop.addEventListener( 'change', syncMap );
	}
}

document.querySelectorAll( '[data-clinic-finder]' ).forEach( initClinicFinder );
